// Reusable print utility that opens a new window with a print-ready HTML
// document. The generated document uses the Lago logo as header and mirrors
// a professional PO/GRN template. Item tables for PR / PO / SR / GRN all use
// the same shared `itemsTable` renderer for a consistent layout.
import { outletNames, statusLabels } from "./format";

const money = (v) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(v || 0));

const fmtDate = (iso) => {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

const LOGO_URL = `${window.location.origin}/lago-logo.png`;

const BASE_STYLES = `
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; background: #fff; color: #14181c; font-family: 'Helvetica Neue', Arial, sans-serif; }
  .sheet { max-width: 820px; margin: 0 auto; padding: 44px 52px 60px; }
  .doc-header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px solid #14181c; padding-bottom: 22px; margin-bottom: 30px; }
  .brand { display: flex; align-items: center; gap: 18px; }
  .brand img { height: 68px; width: auto; }
  .brand .brand-meta { font-size: 10px; letter-spacing: .28em; text-transform: uppercase; color: #7d8a86; margin-top: 8px; }
  .brand h2 { margin: 0; font-size: 13px; letter-spacing: .18em; font-weight: 700; color: #14181c; text-transform: uppercase; }
  .doc-title { text-align: right; }
  .doc-title small { display: block; font-size: 10px; letter-spacing: .3em; text-transform: uppercase; color: #7d8a86; }
  .doc-title h1 { margin: 6px 0 0; font-family: 'Georgia', serif; font-size: 34px; letter-spacing: -.02em; }
  .doc-title .doc-no { font-size: 15px; letter-spacing: .12em; color: #14181c; margin-top: 4px; font-weight: 600; }
  .meta-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 30px; margin-bottom: 24px; }
  .meta-block { border: 1px solid #d8dedb; border-radius: 4px; padding: 14px 18px; }
  .meta-block h3 { margin: 0 0 10px; font-size: 9px; letter-spacing: .28em; text-transform: uppercase; color: #7d8a86; font-weight: 600; }
  .meta-block .row { display: flex; justify-content: space-between; padding: 4px 0; font-size: 12px; }
  .meta-block .row span { color: #7d8a86; }
  .meta-block .row strong { color: #14181c; font-weight: 600; text-align: right; }

  /* ---- Shared items table (PR / PO / SR / GRN) ---- */
  table.items { width: 100%; border-collapse: collapse; margin-top: 6px; font-size: 12px; }
  table.items thead th {
    background: #eef1f0; color: #14181c; font-weight: 700;
    border: 1px solid #c9d2ce; padding: 7px 8px;
    font-size: 10px; letter-spacing: .12em; text-transform: uppercase; text-align: left;
  }
  table.items thead th.num, table.items tbody td.num, table.items tfoot td.num { text-align: right; }
  table.items tbody td { border: 1px solid #d8dedb; padding: 7px 8px; vertical-align: top; }
  table.items tbody tr:nth-child(even) td { background: #fafcfa; }
  table.items tbody td strong { color: #14181c; }
  table.items tbody td small { display: block; color: #7d8a86; font-size: 10px; margin-top: 2px; }
  table.items tfoot td {
    border: 1px solid #c9d2ce; padding: 8px; font-weight: 700; font-size: 12px;
    background: #eef1f0; text-align: right;
  }

  .notes { margin-top: 24px; padding: 12px 16px; background: #fafcfa; border-left: 3px solid #14181c; font-size: 12px; color: #52605a; }
  .notes h4 { margin: 0 0 4px; font-size: 10px; letter-spacing: .22em; text-transform: uppercase; color: #14181c; }
  .signatures { margin-top: 48px; display: grid; grid-template-columns: repeat(3, 1fr); gap: 26px; }
  .sig { text-align: center; }
  .sig .line { height: 62px; border-bottom: 1px solid #14181c; margin-bottom: 10px; }
  .sig .name { font-size: 12px; color: #14181c; font-weight: 600; letter-spacing: .04em; }
  .sig .role { font-size: 10px; color: #7d8a86; text-transform: uppercase; letter-spacing: .18em; margin-top: 2px; }
  .footer { margin-top: 44px; padding-top: 14px; border-top: 1px solid #eaeeeb; text-align: center; font-size: 10px; letter-spacing: .18em; text-transform: uppercase; color: #7d8a86; }
  .footer strong { color: #14181c; letter-spacing: .18em; }
  .status-badge { display: inline-block; padding: 3px 8px; border-radius: 3px; font-size: 10px; letter-spacing: .12em; text-transform: uppercase; background: #eaeeeb; color: #14181c; margin-left: 8px; font-weight: 600; vertical-align: middle; }
  .approval-list { list-style: none; padding: 0; margin: 12px 0 0; }
  .approval-list li { padding: 10px 12px; border: 1px solid #d8dedb; border-radius: 4px; margin-bottom: 8px; }
  .approval-list .muted { color: #7d8a86; font-size: 11px; margin: 2px 0; }
  .badge-pri { display: inline-block; padding: 3px 10px; border-radius: 999px; background: #eef4f2; font-size: 11px; letter-spacing: .1em; text-transform: uppercase; }

  @media print {
    @page { size: A4; margin: 14mm 12mm; }
    body { font-size: 10.5pt; }
    .sheet { max-width: none; padding: 0; }
    .no-print { display: none !important; }
    .doc-header { margin-bottom: 18px; }
    table.items { font-size: 10pt; }
    table.items thead { display: table-header-group; }
    table.items tr { page-break-inside: avoid; break-inside: avoid; }
    table.items thead th, table.items tbody td, table.items tfoot td { padding: 6px 8px; }
    .signatures { page-break-inside: avoid; break-inside: avoid; }
    .notes { page-break-inside: avoid; break-inside: avoid; }
  }
`;

const headerBlock = (title, docNo) => `
  <div class="doc-header">
    <div class="brand">
      <img src="${LOGO_URL}" alt="Lago Bali" onerror="this.style.display='none'"/>
      <div>
        <h2>Lago Bali</h2>
        <div class="brand-meta">Hotel · F&amp;B Operations</div>
      </div>
    </div>
    <div class="doc-title">
      <small>Official document</small>
      <h1>${title}</h1>
      <div class="doc-no">${docNo}</div>
    </div>
  </div>
`;

const footerBlock = `
  <div class="footer">
    Printed from LAGO BALI system — <strong>Created by NANDA HARY</strong>
  </div>
`;

// Shared items table renderer — used by PR, PO, SR and GRN so the printed
// layout stays consistent across documents.
// columns: [{ label, className, width }]
// bodyRows: array of <tr>...</tr> strings
// footerRow: optional <tr>...</tr> string rendered inside <tfoot>
function itemsTable(columns, bodyRows, footerRow, colCount) {
  const head = columns
    .map(
      (c) =>
        `<th class="${c.className || ""}"${c.width ? ` style="width:${c.width}"` : ""}>${c.label}</th>`
    )
    .join("");
  const body =
    bodyRows ||
    `<tr><td colspan="${colCount}" style="text-align:center;padding:22px;color:#7d8a86">No items.</td></tr>`;
  return `
    <table class="items">
      <thead><tr>${head}</tr></thead>
      <tbody>${body}</tbody>
      ${footerRow ? `<tfoot>${footerRow}</tfoot>` : ""}
    </table>`;
}

function openAndPrint(html, title) {
  const w = window.open("", "_blank", "width=980,height=1100");
  if (!w) {
    alert("Popup blocked by browser. Please allow popups to print.");
    return;
  }
  w.document.open();
  w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${title}</title><style>${BASE_STYLES}</style></head><body><div class="sheet">${html}</div><script>window.addEventListener('load',()=>{setTimeout(()=>{window.focus();window.print();},250)});<\/script></body></html>`);
  w.document.close();
}

// ---------- Purchase Order ----------
export function printPurchaseOrder(po, supplier = null) {
  const items = po.items || [];
  const rows = items
    .map((l, i) => {
      const subtotal = Number(l.qty || 0) * Number(l.price || 0);
      return `
    <tr>
      <td>${i + 1}</td>
      <td>
        <strong>${escapeHtml(l.name)}</strong>
        ${l.received_qty ? `<small>Received so far: ${l.received_qty} ${escapeHtml(l.unit || "")}</small>` : ""}
      </td>
      <td class="num">${l.qty}</td>
      <td>${escapeHtml(l.unit || "")}</td>
      <td class="num">${money(l.price)}</td>
      <td class="num"><strong>${money(subtotal)}</strong></td>
    </tr>`;
    })
    .join("");

  const html = `
    ${headerBlock("Purchase Order", po.po_number || "-")}
    <div class="meta-grid">
      <div class="meta-block">
        <h3>PO Information</h3>
        <div class="row"><span>Number</span><strong>${escapeHtml(po.po_number || "-")}</strong></div>
        <div class="row"><span>Date</span><strong>${fmtDate(po.created_at)}</strong></div>
        <div class="row"><span>Destination outlet</span><strong>${escapeHtml(outletNames[po.outlet_code] || po.outlet_code)}</strong></div>
        <div class="row"><span>Status</span><strong>${escapeHtml(statusLabels[po.status] || po.status)}</strong></div>
      </div>
      <div class="meta-block">
        <h3>Supplier</h3>
        <div class="row"><span>Name</span><strong>${escapeHtml(po.supplier || "-")}</strong></div>
        ${supplier?.contact_person ? `<div class="row"><span>Contact</span><strong>${escapeHtml(supplier.contact_person)}</strong></div>` : ""}
        ${supplier?.phone ? `<div class="row"><span>Phone</span><strong>${escapeHtml(supplier.phone)}</strong></div>` : ""}
        ${supplier?.email ? `<div class="row"><span>Email</span><strong>${escapeHtml(supplier.email)}</strong></div>` : ""}
        ${(po?.payment_terms || supplier?.payment_terms) ? `<div class="row"><span>Payment</span><strong>${escapeHtml(po?.payment_terms || supplier?.payment_terms)}</strong></div>` : ""}
        ${supplier?.lead_time_days ? `<div class="row"><span>Lead time</span><strong>${supplier.lead_time_days} days</strong></div>` : ""}
      </div>
    </div>

    ${itemsTable(
      [
        { label: "No", width: "36px" },
        { label: "Item" },
        { label: "Qty", className: "num", width: "70px" },
        { label: "UOM", width: "80px" },
        { label: "Unit price", className: "num", width: "120px" },
        { label: "Subtotal", className: "num", width: "140px" },
      ],
      rows,
      `<tr><td colspan="5">GRAND TOTAL — ${items.length} line(s)</td><td class="num">${money(po.total)}</td></tr>`,
      6
    )}

    ${po.notes ? `<div class="notes"><h4>Notes</h4>${escapeHtml(po.notes)}</div>` : ""}

    <div class="signatures">
      <div class="sig"><div class="line"></div><div class="name">${escapeHtml(po.created_by || "Purchasing")}</div><div class="role">Created by</div></div>
      <div class="sig"><div class="line"></div><div class="name">${escapeHtml(po.approved_by || "_______________")}</div><div class="role">Approved (Finance)</div></div>
      <div class="sig"><div class="line"></div><div class="name">_______________</div><div class="role">Supplier</div></div>
    </div>
    ${footerBlock}
  `;
  openAndPrint(html, `PO ${po.po_number || ""}`);
}

// ---------- Goods Received Note ----------
export function printGRN(grn) {
  const items = grn.items || [];
  const totalQty = items.reduce((s, l) => s + Number(l.qty || 0), 0);
  const rows = items
    .map((l, i) => {
      const subtotal = Number(l.qty || 0) * Number(l.price || 0);
      return `
    <tr>
      <td>${i + 1}</td>
      <td>
        <strong>${escapeHtml(l.name)}</strong>
        ${l.new_avg_cost ? `<small>New COGS: ${money(l.new_avg_cost)} / ${escapeHtml(l.unit || "")}</small>` : ""}
      </td>
      <td class="num">${l.qty}</td>
      <td>${escapeHtml(l.unit || "")}</td>
      <td class="num">${money(l.price)}</td>
      <td class="num"><strong>${money(subtotal)}</strong></td>
    </tr>`;
    })
    .join("");

  const html = `
    ${headerBlock("Goods Received Note", grn.grn_number || "-")}
    <div class="meta-grid">
      <div class="meta-block">
        <h3>Receiving information</h3>
        <div class="row"><span>GRN Number</span><strong>${escapeHtml(grn.grn_number || "-")}</strong></div>
        <div class="row"><span>Received date</span><strong>${fmtDate(grn.received_at)}</strong></div>
        <div class="row"><span>Destination outlet</span><strong>${escapeHtml(outletNames[grn.outlet_code] || grn.outlet_code)}</strong></div>
        ${grn.po_number ? `<div class="row"><span>PO Ref.</span><strong>${escapeHtml(grn.po_number)}</strong></div>` : ""}
        ${grn.document_number ? `<div class="row"><span>${escapeHtml(grn.document_type || "Invoice")} No.</span><strong>${escapeHtml(grn.document_number)}</strong></div>` : ""}
        ${grn.document_date ? `<div class="row"><span>Document date</span><strong>${escapeHtml(grn.document_date)}</strong></div>` : ""}
        <div class="row"><span>Received by</span><strong>${escapeHtml(grn.received_by || "-")}</strong></div>
      </div>
      <div class="meta-block">
        <h3>Supplier</h3>
        <div class="row"><span>Name</span><strong>${escapeHtml(grn.supplier || "-")}</strong></div>
        <div class="row"><span>Total lines</span><strong>${items.length} item</strong></div>
        <div class="row"><span>Total qty received</span><strong>${totalQty}</strong></div>
      </div>
    </div>

    ${itemsTable(
      [
        { label: "No", width: "36px" },
        { label: "Item" },
        { label: "Qty received", className: "num", width: "90px" },
        { label: "UOM", width: "80px" },
        { label: "Unit price", className: "num", width: "120px" },
        { label: "Subtotal", className: "num", width: "140px" },
      ],
      rows,
      `<tr><td colspan="5">GRAND TOTAL — ${items.length} line(s)</td><td class="num">${money(grn.total)}</td></tr>`,
      6
    )}

    ${grn.notes ? `<div class="notes"><h4>Receiving notes</h4>${escapeHtml(grn.notes)}</div>` : ""}

    <div class="signatures">
      <div class="sig"><div class="line"></div><div class="name">${escapeHtml(grn.received_by || "_______________")}</div><div class="role">Warehouse officer</div></div>
      <div class="sig"><div class="line"></div><div class="name">_______________</div><div class="role">Checker</div></div>
      <div class="sig"><div class="line"></div><div class="name">_______________</div><div class="role">Supplier / Courier</div></div>
    </div>
    ${footerBlock}
  `;
  openAndPrint(html, `GRN ${grn.grn_number || ""}`);
}

// ---------- Purchase Request ----------
export function printPurchaseRequest(pr, items = []) {
  // Live Main Warehouse on-hand qty per item (item.stock IS the main-warehouse
  // balance — outlets carry no standing balance by design). Informational only.
  const itemById = new Map(items.map((i) => [i.id, i]));
  const itemBySku = new Map(items.filter((i) => i.sku).map((i) => [i.sku, i]));
  const stockOf = (l) => {
    const m = (l.item_id && itemById.get(l.item_id)) || (l.sku && itemBySku.get(l.sku)) || null;
    if (!m) return `0 ${l.unit || ""}`.trim();
    return `${Number(m.stock || 0)} ${m.unit || l.unit || ""}`.trim();
  };
  const lines = pr.items || [];
  let grandTotal = 0;
  let hasPriced = false;
  const rows = lines
    .map((l, i) => {
      const price = l.price != null && l.price !== "" ? Number(l.price) : null;
      const lineTotal = price != null ? Number(l.qty || 0) * price : null;
      if (lineTotal != null) {
        grandTotal += lineTotal;
        hasPriced = true;
      }
      return `
      <tr>
        <td>${i + 1}</td>
        <td>
          <strong>${escapeHtml(l.name)}</strong>
          ${l.notes ? `<small>${escapeHtml(l.notes)}</small>` : ""}
        </td>
        <td class="num">${escapeHtml(stockOf(l))}</td>
        <td class="num">${l.qty}</td>
        <td>${escapeHtml(l.unit || "")}</td>
        <td class="num">${price != null ? money(price) : "-"}</td>
        <td class="num">${lineTotal != null ? `<strong>${money(lineTotal)}</strong>` : "-"}</td>
      </tr>`;
    })
    .join("");

  const approvals = (pr.approvals || [])
    .map(
      (a) => `
      <li>
        <strong>Level ${a.level + 1} · ${escapeHtml(a.role)} — ${escapeHtml(a.decision)}</strong>
        <div class="muted">${escapeHtml(a.decided_by)} · ${fmtDate(a.decided_at)}</div>
        ${a.comment ? `<div>“${escapeHtml(a.comment)}”</div>` : ""}
      </li>`
    )
    .join("");

  const html = `
    ${headerBlock("Purchase Request", pr.pr_number || "-")}
    <div class="meta-grid">
      <div class="meta-block">
        <h3>Requester</h3>
        <div class="row"><span>Name</span><strong>${escapeHtml(pr.requester_name || "-")}</strong></div>
        <div class="row"><span>Username</span><strong>${escapeHtml(pr.requester_username || "-")}</strong></div>
        <div class="row"><span>Outlet</span><strong>${escapeHtml(outletNames[pr.outlet_code] || pr.outlet_code || pr.department || "-")}</strong></div>
        ${pr.cost_center ? `<div class="row"><span>Cost center</span><strong>${escapeHtml(pr.cost_center)}</strong></div>` : ""}
      </div>
      <div class="meta-block">
        <h3>Request information</h3>
        <div class="row"><span>Requested</span><strong>${fmtDate(pr.request_date)}</strong></div>
        ${pr.required_delivery_date ? `<div class="row"><span>Required by</span><strong>${fmtDate(pr.required_delivery_date)}</strong></div>` : ""}
        ${pr.project ? `<div class="row"><span>Project</span><strong>${escapeHtml(pr.project)}</strong></div>` : ""}
        <div class="row"><span>Priority</span><strong><span class="badge-pri">${escapeHtml(pr.priority)}</span></strong></div>
        <div class="row"><span>Status</span><strong>${escapeHtml(pr.status)}</strong></div>
      </div>
    </div>
    ${pr.notes ? `<div class="notes"><h4>Remarks</h4>${escapeHtml(pr.notes)}</div>` : ""}

    ${itemsTable(
      [
        { label: "No", width: "36px" },
        { label: "Item" },
        { label: "Stock balance", className: "num", width: "90px" },
        { label: "Qty", className: "num", width: "60px" },
        { label: "UOM", width: "70px" },
        { label: "Unit price", className: "num", width: "115px" },
        { label: "Total price", className: "num", width: "130px" },
      ],
      rows,
      `<tr><td colspan="6">TOTAL PURCHASE REQUEST</td><td class="num">${hasPriced ? money(grandTotal) : "-"}</td></tr>`,
      7
    )}

    <div class="notes"><h4>Approval flow — ${(pr.approval_flow || []).map((r) => escapeHtml(r).toUpperCase()).join(" → ")}</h4>
      <ul class="approval-list">${approvals || "<li>No approval decisions recorded yet.</li>"}</ul>
    </div>

    <div class="signatures">
      <div class="sig"><div class="line"></div><div class="name">${escapeHtml(pr.requester_name || "-")}</div><div class="role">Requester</div></div>
      <div class="sig"><div class="line"></div><div class="name">_______________</div><div class="role">Approver</div></div>
      <div class="sig"><div class="line"></div><div class="name">_______________</div><div class="role">Purchasing</div></div>
    </div>
    ${footerBlock}
  `;
  openAndPrint(html, `PR ${pr.pr_number || ""}`);
}

// ---------- Store Request ----------
export function printStoreRequest(sr) {
  const rows = (sr.items || [])
    .map((l, i) => {
      const notes = [];
      if (l.notes) notes.push(escapeHtml(l.notes));
      if (l.qty_approved != null && Number(l.qty_approved) !== Number(l.qty))
        notes.push(`Approved qty: ${l.qty_approved} ${escapeHtml(l.unit || "")}`);
      return `
      <tr>
        <td>${i + 1}</td>
        <td>
          <strong>${escapeHtml(l.name)}</strong>
          ${l.sku ? `<small>SKU ${escapeHtml(l.sku)}</small>` : ""}
        </td>
        <td class="num">${l.qty}</td>
        <td>${escapeHtml(l.unit || "")}</td>
        <td>${notes.length ? notes.join("<br/>") : "-"}</td>
      </tr>`;
    })
    .join("");

  const html = `
    ${headerBlock("Store Request", sr.sr_number || "-")}
    <div class="meta-grid">
      <div class="meta-block">
        <h3>Requester</h3>
        <div class="row"><span>Name</span><strong>${escapeHtml(sr.requester_name || "-")}</strong></div>
        <div class="row"><span>Username</span><strong>${escapeHtml(sr.requester_username || "-")}</strong></div>
        <div class="row"><span>From outlet</span><strong>${escapeHtml(outletNames[sr.from_outlet] || sr.from_outlet || "-")}</strong></div>
        <div class="row"><span>To outlet</span><strong>${escapeHtml(outletNames[sr.to_outlet] || sr.to_outlet || "-")}</strong></div>
      </div>
      <div class="meta-block">
        <h3>Request info</h3>
        <div class="row"><span>Requested</span><strong>${fmtDate(sr.created_at)}</strong></div>
        <div class="row"><span>Priority</span><strong><span class="badge-pri">${escapeHtml(sr.priority || "-")}</span></strong></div>
        <div class="row"><span>Status</span><strong>${escapeHtml(sr.status || "-")}</strong></div>
        ${sr.fulfilled_issue_number ? `<div class="row"><span>Fulfilled by</span><strong>${escapeHtml(sr.fulfilled_issue_number)}</strong></div>` : ""}
        ${sr.reviewed_by ? `<div class="row"><span>Validated by</span><strong>${escapeHtml(sr.reviewed_by)}</strong></div>` : ""}
      </div>
    </div>
    ${sr.notes ? `<div class="notes"><h4>Remarks</h4>${escapeHtml(sr.notes)}</div>` : ""}

    ${itemsTable(
      [
        { label: "No", width: "36px" },
        { label: "Item" },
        { label: "Qty", className: "num", width: "70px" },
        { label: "UOM", width: "80px" },
        { label: "Notes", width: "220px" },
      ],
      rows,
      null,
      5
    )}

    ${sr.review_comment ? `<div class="notes"><h4>Warehouse comment</h4>${escapeHtml(sr.review_comment)}</div>` : ""}
    ${sr.reject_reason ? `<div class="notes"><h4>Reject reason</h4>${escapeHtml(sr.reject_reason)}</div>` : ""}

    <div class="signatures">
      <div class="sig"><div class="line"></div><div class="name">${escapeHtml(sr.requester_name || "-")}</div><div class="role">Requester</div></div>
      <div class="sig"><div class="line"></div><div class="name">${escapeHtml(sr.reviewed_by || "_______________")}</div><div class="role">Warehouse validator</div></div>
      <div class="sig"><div class="line"></div><div class="name">_______________</div><div class="role">Receiver</div></div>
    </div>
    ${footerBlock}
  `;
  openAndPrint(html, `SR ${sr.sr_number || ""}`);
}

function escapeHtml(s) {
  return String(s ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

// ---------- Generic report print (A4, browser "Save as PDF") ----------
// columns: [{ label, className }]; bodyRows: array of <tr> strings (already escaped)
export function printReport({ title, period, printedBy, filters = [], columns, bodyRows, footerRow, emptyText = "No data." }) {
  const printedAt = new Date().toLocaleString("en-GB");
  const filterRows = filters
    .filter((f) => f && f.value)
    .map((f) => `<div class="row"><span>${escapeHtml(f.label)}</span><strong>${escapeHtml(f.value)}</strong></div>`)
    .join("");
  const html = `
    ${headerBlock(escapeHtml(title), "REPORT")}
    <div class="meta-grid">
      <div class="meta-block">
        <h3>Report period</h3>
        <div class="row"><span>From</span><strong>${fmtDate(period.start)}</strong></div>
        <div class="row"><span>To</span><strong>${fmtDate(period.end)}</strong></div>
        ${filterRows}
      </div>
      <div class="meta-block">
        <h3>Print info</h3>
        <div class="row"><span>Printed at</span><strong>${escapeHtml(printedAt)}</strong></div>
        <div class="row"><span>Printed by</span><strong>${escapeHtml(printedBy || "-")}</strong></div>
        <div class="row"><span>Currency</span><strong>IDR (Rp)</strong></div>
      </div>
    </div>
    ${itemsTable(columns, bodyRows.length ? bodyRows.join("") : null, footerRow, columns.length)}
    ${bodyRows.length ? "" : `<p style="text-align:center;color:#7d8a86;font-size:12px">${escapeHtml(emptyText)}</p>`}
    ${footerBlock}
  `;
  openAndPrint(html, title);
}
export { escapeHtml };
